/**
 * Progressive Feature Loader
 * Performance optimization: Load non-critical features only when needed
 * Uses Intersection Observer to detect when features scroll into view
 *
 * @version 1.0.0
 * @shopify-performance-optimization
 */

(function() {
  'use strict';

  // Feature definitions with selectors and initialization logic
  const featureLoaders = {
    // Video players (YouTube, Vimeo, HTML5)
    videos: {
      selector: '[data-video-id], .video-wrapper, [data-video], .hero-video',
      load: function() {
        console.log('[Performance] Loading video players');

        // Initialize theme video player if available
        if (typeof theme !== 'undefined' && theme.VideoPlayer) {
          theme.VideoPlayer.init();
        }

        // Initialize any YouTube players
        if (window.YT && window.YT.Player) {
          document.querySelectorAll('[data-video-id]').forEach(function(el) {
            if (!el.dataset.initialized) {
              el.dataset.initialized = 'true';
              // Video initialization will be handled by theme.VideoPlayer
            }
          });
        }
      }
    },

    // Slideshow/carousel functionality
    slideshow: {
      selector: '.slideshow-wrapper, [data-slideshow], .flickity-enabled',
      load: function() {
        console.log('[Performance] Loading slideshow');

        if (typeof theme !== 'undefined' && theme.Slideshow && typeof theme.Slideshow.init === 'function') {
          theme.Slideshow.init();
        }

        // Initialize Flickity if used
        if (typeof Flickity !== 'undefined') {
          document.querySelectorAll('[data-slideshow]').forEach(function(el) {
            if (!el.dataset.flickityInitialized) {
              new Flickity(el, {
                cellAlign: 'left',
                contain: true,
                pageDots: true,
                prevNextButtons: true
              });
            }
          });
        }
      }
    },

    // Advanced animations (AOS, custom animations)
    animations: {
      selector: '[data-aos], .aos-animate, [data-animate]',
      load: function() {
        console.log('[Performance] Loading animations');

        // Initialize AOS if available
        if (typeof AOS !== 'undefined') {
          AOS.init({
            duration: 400,
            easing: 'ease-out',
            once: true,
            offset: 100
          });
        }

        // Initialize custom theme animations
        if (typeof theme !== 'undefined' && theme.animations) {
          theme.animations.init();
        }
      }
    },

    // Product recommendations
    recommendations: {
      selector: '[data-product-recommendations], .product-recommendations',
      load: function() {
        console.log('[Performance] Loading product recommendations');

        if (typeof theme !== 'undefined' && theme.ProductRecommendations) {
          theme.ProductRecommendations.init();
        }
      }
    },

    // Modal/lightbox functionality
    modals: {
      selector: '[data-modal-trigger], [data-lightbox], .photoswipe-wrapper',
      load: function() {
        console.log('[Performance] Loading modal/lightbox');

        // Initialize PhotoSwipe if available
        if (typeof PhotoSwipe !== 'undefined' && typeof theme !== 'undefined' && theme.Photoswipe) {
          theme.Photoswipe.init();
        }

        // Initialize quick shop modals
        if (typeof theme !== 'undefined' && theme.QuickShop) {
          theme.QuickShop.init();
        }
      }
    }
  };

  /**
   * Initialize progressive feature loading using Intersection Observer
   */
  function initFeatureLoader() {
    // Fallback for browsers without Intersection Observer support
    if (!('IntersectionObserver' in window)) {
      console.log('[Performance] IntersectionObserver not supported, loading all features');
      Object.values(featureLoaders).forEach(function(feature) {
        feature.load();
      });
      return;
    }

    const observerOptions = {
      rootMargin: '200px', // Load 200px before element enters viewport
      threshold: 0.01 // Trigger when even 1% is visible
    };

    // Track which features have been loaded
    const loadedFeatures = new Set();

    Object.entries(featureLoaders).forEach(function([name, feature]) {
      const elements = document.querySelectorAll(feature.selector);

      // Skip if no elements found for this feature
      if (elements.length === 0) {
        console.log('[Performance] No elements found for feature: ' + name);
        return;
      }

      console.log('[Performance] Found ' + elements.length + ' elements for feature: ' + name);

      const observer = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting && !loadedFeatures.has(name)) {
            console.log('[Performance] Element visible, loading feature: ' + name);
            loadedFeatures.add(name);
            feature.load();
            observer.disconnect(); // Load once and stop observing
          }
        });
      }, observerOptions);

      // Observe first element of this type (others will be initialized when first loads)
      observer.observe(elements[0]);
    });
  }

  /**
   * Initialize when DOM is ready
   */
  function init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initFeatureLoader);
    } else {
      // DOM already loaded
      initFeatureLoader();
    }
  }

  // Start initialization
  init();
})();
